import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Autocomplete,
  Chip,
  CircularProgress,
} from '@mui/material';
import { apiService } from '../../services/apiService';

export const EMPTY_INDICATOR_OPTIONS = {
  types: [],
  sources: [],
  tlp: ['WHITE', 'GREEN', 'AMBER', 'RED'],
  confidences: ['Low', 'Medium', 'High'],
  severities: ['Low', 'Medium', 'High', 'Critical'],
  statuses: ['Active', 'Inactive', 'Expired', 'False Positive'],
};

/**
 * Turn an axios error from the indicators API into a message for the user.
 * 409 -> "Indicator already exists"; 400 -> joined validator messages.
 */
export const getIndicatorErrorMessage = (err, fallback = 'Request failed') => {
  const data = err?.response?.data;
  if (err?.response?.status === 409) return data?.message || 'Indicator already exists';
  if (Array.isArray(data?.errors) && data.errors.length) {
    return data.errors.map((e) => e.msg).join('; ');
  }
  return data?.message || err?.message || fallback;
};

const emptyForm = {
  type: '',
  value: '',
  description: '',
  source: '',
  tlp: 'WHITE',
  confidence: 'Medium',
  severity: 'Medium',
  status: 'Active',
  tags: [],
};

const toForm = (indicator) => ({
  type: indicator?.type || '',
  value: indicator?.value || '',
  description: indicator?.description || '',
  source: indicator?.source || '',
  tlp: indicator?.tlp || 'WHITE',
  confidence: indicator?.confidence || 'Medium',
  severity: indicator?.severity || 'Medium',
  status: indicator?.status || 'Active',
  tags: Array.isArray(indicator?.tags) ? indicator.tags : [],
});

/**
 * Create / edit dialog for an indicator.
 *
 * Props:
 *  - open, onClose
 *  - indicator: existing indicator to edit (null/undefined to create)
 *  - options: result of GET /indicators/options (fetched here if omitted)
 *  - onSaved(indicator): called with the saved indicator from the server
 */
export const IndicatorDialog = ({ open, onClose, indicator, options: optionsProp, onSaved }) => {
  const isEdit = Boolean(indicator?._id);
  const [form, setForm] = useState(emptyForm);
  const [options, setOptions] = useState(optionsProp || EMPTY_INDICATOR_OPTIONS);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(toForm(indicator));
      setError('');
    }
  }, [open, indicator]);

  useEffect(() => {
    if (optionsProp) {
      setOptions(optionsProp);
      return;
    }
    if (!open) return;
    let cancelled = false;
    setLoadingOptions(true);
    apiService.indicators.getOptions()
      .then((res) => { if (!cancelled) setOptions({ ...EMPTY_INDICATOR_OPTIONS, ...res.data }); })
      .catch((err) => console.error('Failed to load indicator options:', err))
      .finally(() => { if (!cancelled) setLoadingOptions(false); });
    return () => { cancelled = true; };
  }, [open, optionsProp]);

  const setField = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.type || !form.value.trim() || !form.source) {
      setError('Type, value and source are required.');
      return;
    }
    // Omit empty optional values: the API validators reject '' for enum fields.
    const payload = {};
    Object.entries(form).forEach(([key, val]) => {
      if (Array.isArray(val)) payload[key] = val;
      else if (typeof val === 'string' && val.trim() !== '') payload[key] = val.trim();
    });

    try {
      setSaving(true);
      setError('');
      const response = isEdit
        ? await apiService.indicators.update(indicator._id, payload)
        : await apiService.indicators.create(payload);
      if (onSaved) onSaved(response.data.indicator);
      onClose();
    } catch (err) {
      console.error('Failed to save indicator:', err);
      setError(getIndicatorErrorMessage(err, isEdit ? 'Failed to update indicator' : 'Failed to create indicator'));
    } finally {
      setSaving(false);
    }
  };

  const renderSelect = (label, field, values, disabled = false) => (
    <FormControl fullWidth disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select value={form[field]} label={label} onChange={setField(field)}>
        {(values || []).map((v) => (
          <MenuItem key={v} value={v}>{v}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Indicator' : 'New Indicator'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loadingOptions && (
          <CircularProgress size={20} sx={{ mb: 2 }} />
        )}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={6}>
            {renderSelect('Type', 'type', options.types, isEdit)}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderSelect('Source', 'source', options.sources)}
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Value"
              value={form.value}
              onChange={setField('value')}
              disabled={isEdit}
              inputProps={{ maxLength: 1000, style: { fontFamily: 'monospace' } }}
              helperText={isEdit ? 'Type and value cannot be changed after creation' : undefined}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Description"
              value={form.description}
              onChange={setField('description')}
              inputProps={{ maxLength: 2000 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderSelect('TLP', 'tlp', options.tlp)}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderSelect('Confidence', 'confidence', options.confidences)}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderSelect('Severity', 'severity', options.severities)}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {renderSelect('Status', 'status', options.statuses)}
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={form.tags}
              onChange={(e, value) => setForm((prev) => ({ ...prev, tags: value }))}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" size="small" label={option} {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Tags" placeholder="Type a tag and press Enter" />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Indicator'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IndicatorDialog;
